import {
  type TransactionEvent,
  TransactionEvent as TransactionEventSchema,
  type WalletEvent,
  WalletEvent as WalletEventSchema,
} from "@ggcoin/events";
import type { TransactionEventListener, Unsubscribe, WalletEventListener } from "./client.ts";
import { GGCoinError } from "./errors.ts";

/** Inputs for {@link streamTransactionsEvents}. */
export interface StreamTransactionsInput {
  baseUrl: string;
  token: string;
  onEvent: TransactionEventListener;
  onError?: (err: Error) => void;
  /** Inject a custom EventSource (tests / SSR). Defaults to `globalThis.EventSource`. */
  EventSourceImpl?: typeof EventSource;
}

/**
 * Open `GET /events/transactions?token=<jwt>` via EventSource. Each `event:`
 * line on the wire dispatches as a `MessageEvent` typed by event name;
 * payloads are JSON with `bigint` fields encoded as decimal strings. We
 * parse + Zod-validate so the listener sees domain-shape events with
 * `bigint` amounts.
 */
export function streamTransactionsEvents(input: StreamTransactionsInput): Unsubscribe {
  const ES = input.EventSourceImpl ?? globalThis.EventSource;
  if (!ES) {
    throw new GGCoinError(
      "MISSING_EVENT_SOURCE",
      0,
      "no EventSource available; pass `EventSource` in the client config",
    );
  }
  const url = `${input.baseUrl}/events/transactions?token=${encodeURIComponent(input.token)}`;
  const es = new ES(url);

  const dispatch = (raw: MessageEvent): void => {
    try {
      const data = typeof raw.data === "string" ? raw.data : "";
      if (!data) return;
      const parsed = JSON.parse(data) as Record<string, unknown>;
      coerceMoneyFields(parsed);
      const event = TransactionEventSchema.parse(parsed) as TransactionEvent;
      input.onEvent(event);
    } catch (err) {
      input.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  };

  es.addEventListener("transaction.posted", dispatch as EventListener);
  es.addEventListener("transaction.reversed", dispatch as EventListener);
  es.addEventListener("error", () => {
    input.onError?.(new GGCoinError("SSE_ERROR", 0, "EventSource error"));
  });

  return () => {
    es.close();
  };
}

/** Inputs for {@link subscribeWalletEvents}. */
export interface SubscribeWalletInput {
  baseUrl: string;
  token: string;
  onEvent: WalletEventListener;
  onError?: (err: Error) => void;
  /** Inject a custom WebSocket (tests / SSR). Defaults to `globalThis.WebSocket`. */
  WebSocketImpl?: typeof WebSocket;
}

/**
 * Open the `/ws/wallet?token=<jwt>` WebSocket. Resolves once the socket is
 * open; the returned `Unsubscribe` closes it. Inbound messages are JSON
 * with `bigint` fields encoded as decimal strings — same coercion +
 * Zod-validate path as the SSE handler.
 */
export function subscribeWalletEvents(input: SubscribeWalletInput): Promise<Unsubscribe> {
  const WSImpl = input.WebSocketImpl ?? globalThis.WebSocket;
  if (!WSImpl) {
    throw new GGCoinError(
      "MISSING_WEBSOCKET",
      0,
      "no WebSocket available; pass `WebSocket` in the client config",
    );
  }
  const url = `${toWsUrl(input.baseUrl)}/ws/wallet?token=${encodeURIComponent(input.token)}`;
  const ws = new WSImpl(url);

  ws.addEventListener("message", (evt) => {
    try {
      const data = typeof evt.data === "string" ? evt.data : "";
      if (!data) return;
      const parsed = JSON.parse(data) as Record<string, unknown>;
      coerceMoneyFields(parsed);
      const event = WalletEventSchema.parse(parsed) as WalletEvent;
      input.onEvent(event);
    } catch (err) {
      input.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  });
  ws.addEventListener("error", () => {
    input.onError?.(new GGCoinError("WS_ERROR", 0, "WebSocket error"));
  });

  return new Promise((resolve, reject) => {
    ws.addEventListener("open", () => {
      resolve(() => {
        ws.close();
      });
    });
    ws.addEventListener("close", (evt) => {
      // 1008 = policy-violation, used by the server when JWT verify fails
      // during the upgrade handshake.
      if (evt.code === 1008) {
        reject(new GGCoinError("UNAUTHENTICATED", 401, "websocket rejected token"));
      }
    });
  });
}

/** http → ws, https → wss. Leaves anything else (already ws/wss) untouched. */
function toWsUrl(baseUrl: string): string {
  if (baseUrl.startsWith("https://")) return `wss://${baseUrl.slice("https://".length)}`;
  if (baseUrl.startsWith("http://")) return `ws://${baseUrl.slice("http://".length)}`;
  return baseUrl;
}

/**
 * Walk a parsed JSON event and coerce known bigint fields back from their
 * decimal-string wire form so the Zod schemas can validate them.
 */
function coerceMoneyFields(obj: Record<string, unknown>): void {
  coerceBigInt(obj, "amount");
  coerceBigInt(obj, "balance");
  if (Array.isArray(obj.entries)) {
    for (const entry of obj.entries) {
      if (entry && typeof entry === "object") {
        coerceBigInt(entry as Record<string, unknown>, "amount");
      }
    }
  }
}

/** In-place coerce `obj[key]` from a decimal string to a bigint, if present. */
function coerceBigInt(obj: Record<string, unknown>, key: string): void {
  const raw = obj[key];
  if (typeof raw === "string") {
    obj[key] = BigInt(raw);
  }
}
